/**
 * Hero banner showing the key CashFlow metrics.
 * The Monthly CashFlow number is the hero — big, colour-coded, unmissable.
 */

import type { DerivedMetrics } from '../../types/cashflow';
import { formatCurrency, formatPct, formatMonth, cashflowBannerColor } from '../../utils/cashflowCalc';

interface Props {
  metrics: DerivedMetrics;
  isDemo: boolean;
}

export default function HeroBanner({ metrics, isDemo }: Props) {
  const bgColor = cashflowBannerColor(metrics.monthlyCashflow);
  const cashflowPositive = metrics.monthlyCashflow >= 0;

  return (
    <div
      style={{
        background: bgColor,
        color: '#fff',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-xl)',
        marginBottom: 'var(--spacing-lg)',
        position: 'relative',
      }}
    >
      {isDemo && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 'var(--radius-sm)',
            padding: '2px 10px',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
          }}
        >
          DEMO DATA
        </div>
      )}

      <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: 4 }}>
        {formatMonth(metrics.month)} · Monthly CashFlow
      </div>

      <div
        style={{
          fontSize: '3.5rem',
          fontWeight: 800,
          letterSpacing: '-0.02em',
          lineHeight: 1,
          marginBottom: 'var(--spacing-lg)',
        }}
      >
        {cashflowPositive ? '+' : ''}
        {formatCurrency(metrics.monthlyCashflow)}
      </div>

      <div style={{ display: 'flex', gap: 'var(--spacing-xl)', flexWrap: 'wrap' }}>
        <Metric
          label="Passive Income Ratio"
          value={formatPct(metrics.passiveIncomeRatio)}
          sub="of income is passive"
        />
        <Metric
          label="Financial Freedom"
          value={formatPct(metrics.financialFreedomPct)}
          sub={metrics.financialFreedomPct >= 1 ? '🎉 Out of the rat race!' : 'of expenses covered by passive income'}
        />
        <Metric label="Net Worth" value={formatCurrency(metrics.netWorth)} sub="assets minus liabilities" />
        <Metric label="Total Assets" value={formatCurrency(metrics.totalAssets)} sub="" />
        <Metric label="Total Liabilities" value={formatCurrency(metrics.totalLiabilities)} sub="" />
      </div>
    </div>
  );
}

function Metric({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div>
      <div style={{ fontSize: '0.75rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{sub}</div>}
    </div>
  );
}
